import { useState, useEffect } from "react";
import { PROJECT_LIST, getProject } from "./projectConfig";
import { AppToolbarButton } from "./WhatsNew";

const NGI_BLUE = "#002855";
const NGI_RED = "#ab2328";
const NGI_STEEL = "#75787b";

interface DashboardProps {
  onNavigate: (route: string) => void;
}

interface WorkflowItem {
  icon: string;
  title: string;
  description: string;
  route?: string;
  available: boolean;
  mobileRelevant: boolean;
  desktopRelevant: boolean;
}

interface WorkflowPhase {
  id: string;
  title: string;
  subtitle: string;
  color: string;
  icon: string;
  items: WorkflowItem[];
  collapsible?: boolean;
  desktopOnly?: boolean;
  /** For single-action phases: route to navigate to directly (no sub-items shown) */
  route?: string;
  /** Whether this direct-action phase is available */
  directAvailable?: boolean;
  defaultCollapsedMobile?: boolean;
  defaultCollapsedDesktop?: boolean;
}

const PHASES: WorkflowPhase[] = [
  {
    id: "pre",
    title: "Desk study",
    subtitle: "Desktop — plan and review",
    color: NGI_BLUE,
    icon: "1",
    defaultCollapsedMobile: true,
    items: [
      { icon: "\u{1F6E4}", title: "Tunnel line", description: "Centerline alignment and elevation profile", route: "/tunnel-line", available: true, mobileRelevant: true, desktopRelevant: true },
      { icon: "\u{26F0}", title: "Terrain models", description: "Import DEM from file or Kartverket", route: "/terrain", available: true, mobileRelevant: false, desktopRelevant: true },
      { icon: "\u{1F5FA}", title: "Geological maps and sections", description: "Regional geology, weakness zones", available: false, mobileRelevant: true, desktopRelevant: true },
      { icon: "\u{1F4CA}", title: "Geotechnical investigations", description: "Boreholes, lab results, field data", available: false, mobileRelevant: true, desktopRelevant: true },
      { icon: "\u{1F3D7}", title: "Construction models", description: "Tunnel / rock cut geometry and BIM", available: false, mobileRelevant: true, desktopRelevant: true },
    ],
  },
  {
    id: "field",
    title: "GIS Survey",
    subtitle: "Mobile — collect data on site",
    color: "#2e7d32",
    icon: "2",
    collapsible: false,
    route: "/map",
    directAvailable: true,
    items: [],
  },
  {
    id: "geomodel",
    title: "Geological model",
    subtitle: "Desktop — build and refine",
    color: "#7b1fa2",
    icon: "3",
    desktopOnly: true,
    collapsible: false,
    route: "/geo-model",
    directAvailable: true,
    items: [],
  },
  {
    id: "construction",
    title: "3D Survey",
    subtitle: "Both — map and document",
    color: "#5c6bc0",
    icon: "4",
    collapsible: false,
    route: "/tunnel",
    directAvailable: true,
    items: [],
  },
  {
    id: "reporting",
    title: "Reporting",
    subtitle: "Desktop — summarize and deliver",
    color: "#8d6e63",
    icon: "5",
    defaultCollapsedMobile: true,
    defaultCollapsedDesktop: true,
    items: [
      { icon: "\u{1F4D0}", title: "Unwrapped profile", description: "2D flattened tunnel surface view", route: "/utbrettsprofil", available: true, mobileRelevant: true, desktopRelevant: true },
      { icon: "\u{1F4C4}", title: "Geological report", description: "Generate documentation from collected data", available: false, mobileRelevant: false, desktopRelevant: true },
      { icon: "\u{1F4E4}", title: "Data export", description: "Export to GIS, BIM and other formats", available: false, mobileRelevant: false, desktopRelevant: true },
    ],
  },
];

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return mobile;
}

const PROJECTS = PROJECT_LIST;

function ProjectPicker({ isMobile, activeId, onSelect }: { isMobile: boolean; activeId: string; onSelect: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const selected = PROJECTS.find((p) => p.id === activeId) ?? PROJECTS[0];

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: isMobile ? "9px 12px" : "10px 16px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.08)",
          border: "1.5px solid rgba(255,255,255,0.18)",
          color: "#f7f8fb",
          cursor: "pointer",
          fontSize: isMobile ? "0.82rem" : "0.85rem",
          fontWeight: 600,
          width: "100%",
          minWidth: 0,
          textAlign: "left",
        }}
      >
        <span style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#4caf50",
          flexShrink: 0,
        }} />
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected.name}
        </span>
        <span style={{
          fontSize: "0.65rem",
          opacity: 0.5,
          flexShrink: 0,
          transition: "transform 0.15s ease",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
        }}>
          &#9662;
        </span>
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 99 }}
          />
          {/* Dropdown */}
          <div style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 100,
            borderRadius: 12,
            background: "rgba(15,23,38,0.95)",
            border: "1px solid rgba(255,255,255,0.15)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
            padding: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}>
            {PROJECTS.map((project) => (
              <button
                key={project.id}
                type="button"
                onClick={() => { onSelect(project.id); setOpen(false); }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "9px 12px",
                  borderRadius: 8,
                  background: project.id === activeId ? "rgba(255,255,255,0.08)" : "transparent",
                  border: "none",
                  color: "#f7f8fb",
                  cursor: "pointer",
                  fontSize: "0.82rem",
                  fontWeight: project.id === activeId ? 600 : 400,
                  textAlign: "left",
                  minWidth: 0,
                }}
              >
                <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: project.id === activeId ? "#4caf50" : "rgba(255,255,255,0.15)",
                  flexShrink: 0,
                }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {project.name}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Summary rows — pulled from project config */

export function Dashboard({ onNavigate }: DashboardProps) {
  const isMobile = useIsMobile();

  // Read active project from ?project= query param, then localStorage, then default
  const [activeProjectId, setActiveProjectId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("project");
    if (fromUrl && getProject(fromUrl).id === fromUrl) return fromUrl;
    try {
      const saved = localStorage.getItem("rockrecord:active-project");
      if (saved && getProject(saved).id === saved) return saved;
    } catch {}
    return "e16-fas";
  });

  // Sync query param + localStorage when selection changes
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("project", activeProjectId);
    window.history.replaceState(null, "", url.toString());
    try { localStorage.setItem("rockrecord:active-project", activeProjectId); } catch {}
  }, [activeProjectId]);
  const activeProject = getProject(activeProjectId);
  const dataSummary = activeProject.dataSummary;

  // Wrap onNavigate to prefix routes with the active project ID
  const projectNavigate = (route: string) => {
    // Routes in PHASES are like "/tunnel", "/map" — prefix with project
    onNavigate(`/${activeProjectId}${route}`);
  };

  // Always show phases in workflow order 1-2-3
  const phases = PHASES;

  /* ── Mobile layout (unchanged) ── */
  if (isMobile) {
    return (
      <div style={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        background: "linear-gradient(180deg, #0a1628 0%, #0f1d32 50%, #162440 100%)",
        color: "#f7f8fb",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>
        <header style={{
          flexShrink: 0,
          padding: "16px 16px 12px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <svg width={56} height={27} viewBox="0 0 106 50" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="NGI">
                <path d="M51.5912 39.1975L51.4896 39.0653C48.5556 34.9206 46.9869 30.0265 46.9434 24.8971C46.8853 18.2981 49.3835 12.0811 53.9442 7.37801C58.5194 2.6749 64.6197 0.0587889 71.1412 0H71.3446C78.113 0 84.4021 2.73369 89.0645 7.71605L89.1807 7.83363L82.848 13.0511L82.7463 12.9483C79.6817 9.90594 75.6293 8.21576 71.3446 8.21576H71.2139C62.2377 8.28924 55.0045 15.7407 55.0771 24.8089C55.1062 28.057 56.0648 31.1728 57.8368 33.8624L57.9094 33.98L57.8077 34.0682L51.5912 39.1828V39.1975Z" fill="#75787b" />
                <path d="M106 48.7067H97.5469V0.308716H106V48.7067Z" fill="#75787b" />
                <path d="M82.3395 50V34.0829H65.4185L73.8136 26.9401H89.442V42.916L89.3984 42.9601L82.3541 50H82.3395Z" fill="#ab2328" />
                <path d="M32.4623 29.9382L0 0.264526V48.4715H8.07564V19.4444L40.5379 48.3833V0.426196H32.4623V29.9382Z" fill="#75787b" />
              </svg>
              <div>
                <div style={{ fontSize: "1.3rem", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1 }}>RockRecord</div>
                <div style={{ fontSize: "0.65rem", opacity: 0.45, fontWeight: 500, letterSpacing: "0.02em" }}>Geological workflow support</div>
              </div>
            </div>
            <AppToolbarButton />
          </div>
          <div style={{ fontSize: "0.68rem", opacity: 0.45, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" as const, marginBottom: 6 }}>My projects</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ProjectPicker isMobile={true} activeId={activeProjectId} onSelect={setActiveProjectId} />
            <button type="button" disabled style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(247,248,251,0.3)", cursor: "default", fontSize: "0.78rem", fontWeight: 500, flexShrink: 0 }}>+ New</button>
          </div>
        </header>
        <div className="hide-scrollbar" style={{ flex: 1, overflowX: "hidden", overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 16px calc(16px + env(safe-area-inset-bottom, 0px)) 16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, width: "100%", minWidth: 0 }}>
            {phases.map((phase) => (
              <PhaseCard key={phase.id} phase={phase} isMobile={true} onNavigate={projectNavigate} collapsed={!!phase.defaultCollapsedMobile} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── Desktop layout: 3-column ── */
  return (
    <div style={{
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      overflowX: "hidden",
      background: "linear-gradient(180deg, #0a1628 0%, #0f1d32 50%, #162440 100%)",
      color: "#f7f8fb",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      {/* Header: logo + branding */}
      <header style={{
        flexShrink: 0,
        padding: "24px 40px 16px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <svg width={72} height={34} viewBox="0 0 106 50" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="NGI">
            <path d="M51.5912 39.1975L51.4896 39.0653C48.5556 34.9206 46.9869 30.0265 46.9434 24.8971C46.8853 18.2981 49.3835 12.0811 53.9442 7.37801C58.5194 2.6749 64.6197 0.0587889 71.1412 0H71.3446C78.113 0 84.4021 2.73369 89.0645 7.71605L89.1807 7.83363L82.848 13.0511L82.7463 12.9483C79.6817 9.90594 75.6293 8.21576 71.3446 8.21576H71.2139C62.2377 8.28924 55.0045 15.7407 55.0771 24.8089C55.1062 28.057 56.0648 31.1728 57.8368 33.8624L57.9094 33.98L57.8077 34.0682L51.5912 39.1828V39.1975Z" fill="#75787b" />
            <path d="M106 48.7067H97.5469V0.308716H106V48.7067Z" fill="#75787b" />
            <path d="M82.3395 50V34.0829H65.4185L73.8136 26.9401H89.442V42.916L89.3984 42.9601L82.3541 50H82.3395Z" fill="#ab2328" />
            <path d="M32.4623 29.9382L0 0.264526V48.4715H8.07564V19.4444L40.5379 48.3833V0.426196H32.4623V29.9382Z" fill="#75787b" />
          </svg>
          <div>
            <div style={{ fontSize: "1.5rem", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1 }}>RockRecord</div>
            <div style={{ fontSize: "0.7rem", opacity: 0.45, fontWeight: 500, letterSpacing: "0.02em" }}>Geological workflow support</div>
          </div>
        </div>
        <AppToolbarButton />
      </header>

      {/* 3-column body */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>

        {/* ── Left: Project list ── */}
        <div style={{
          width: 240,
          flexShrink: 0,
          borderRight: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          flexDirection: "column",
          padding: "20px 16px",
          overflowY: "auto",
        }}>
          <div style={{ fontSize: "0.68rem", opacity: 0.45, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" as const, marginBottom: 10 }}>
            Projects
          </div>
          {PROJECTS.map((project) => (
            <button
              key={project.id}
              type="button"
              onClick={() => setActiveProjectId(project.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: project.id === activeProjectId ? "rgba(255,255,255,0.08)" : "transparent",
                border: project.id === activeProjectId ? "1px solid rgba(255,255,255,0.15)" : "1px solid transparent",
                color: "#f7f8fb",
                cursor: "pointer",
                fontSize: "0.82rem",
                fontWeight: project.id === activeProjectId ? 600 : 400,
                textAlign: "left",
                width: "100%",
                marginBottom: 4,
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: "50%",
                background: project.id === activeProjectId ? "#4caf50" : "rgba(255,255,255,0.15)",
                flexShrink: 0,
              }} />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {project.name}
              </span>
            </button>
          ))}
          <button
            type="button"
            disabled
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 12px",
              borderRadius: 10,
              background: "transparent",
              border: "1px dashed rgba(255,255,255,0.1)",
              color: "rgba(247,248,251,0.25)",
              cursor: "default",
              fontSize: "0.78rem",
              fontWeight: 500,
              width: "100%",
              marginTop: 4,
            }}
          >
            + New project
          </button>
        </div>

        {/* ── Center: Workflow phases (narrow, mobile-like) ── */}
        <div className="hide-scrollbar" style={{
          width: 440,
          flexShrink: 0,
          overflowY: "auto",
          padding: "24px 24px 32px",
          borderRight: "1px solid rgba(255,255,255,0.06)",
        }}>
          <div style={{ fontSize: "0.68rem", opacity: 0.45, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" as const, marginBottom: 14 }}>
            Workflow phases
          </div>
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}>
            {phases.map((phase) => (
              <PhaseCard key={phase.id} phase={phase} isMobile={false} onNavigate={projectNavigate} collapsed={!!phase.defaultCollapsedDesktop} />
            ))}
          </div>
        </div>

        {/* ── Right: Project data summary ── */}
        <div className="hide-scrollbar" style={{
          flex: 1,
          minWidth: 0,
          borderLeft: "1px solid rgba(255,255,255,0.06)",
          overflowY: "auto",
          padding: "20px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}>
          <div style={{ fontSize: "0.68rem", opacity: 0.45, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" as const }}>
            Project data — {activeProject.name}
          </div>

          <div style={{
            borderRadius: 14,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            overflow: "hidden",
          }}>
            {dataSummary.map((row, i) => (
              <div
                key={row.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  borderBottom: i < dataSummary.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                }}
              >
                <span style={{ flex: 1, fontSize: "0.78rem", fontWeight: 500 }}>{row.label}</span>
                <span style={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: row.available ? "#4caf50" : "rgba(255,255,255,0.35)",
                  opacity: row.available ? 0.85 : 0.5,
                }}>{row.value}</span>
              </div>
            ))}
          </div>

          {/* Quick stats */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
          }}>
            {[
              { label: "Phases", value: "5", sub: "2 active" },
              { label: "Data layers", value: "12", sub: "8 available" },
            ].map((stat) => (
              <div key={stat.label} style={{
                borderRadius: 12,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                padding: "14px 14px 12px",
                textAlign: "center",
              }}>
                <div style={{ fontSize: "1.4rem", fontWeight: 800, letterSpacing: "-0.02em" }}>{stat.value}</div>
                <div style={{ fontSize: "0.68rem", fontWeight: 600, opacity: 0.7, marginTop: 2 }}>{stat.label}</div>
                <div style={{ fontSize: "0.6rem", opacity: 0.4, marginTop: 1 }}>{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PhaseCard({ phase, isMobile, onNavigate, collapsed: defaultCollapsed }: {
  phase: WorkflowPhase;
  isMobile: boolean;
  onNavigate: (route: string) => void;
  collapsed: boolean;
}) {
  const isDirectPhase = phase.route != null && phase.items.length === 0;
  const canCollapse = !isDirectPhase && phase.collapsible !== false;
  const [collapsed, setCollapsed] = useState(canCollapse ? defaultCollapsed : false);

  return (
    <div style={{
      borderRadius: collapsed ? 12 : 16,
      background: collapsed
        ? "rgba(255,255,255,0.03)"
        : "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
      border: `1px solid ${collapsed ? "rgba(255,255,255,0.08)" : phase.color + "33"}`,
      padding: collapsed
        ? (isMobile ? "10px 14px" : "12px 16px")
        : (isMobile ? "14px 16px" : "20px"),
      display: "flex",
      flexDirection: "column",
      gap: collapsed ? 0 : 12,
      minWidth: 0,
      overflow: "hidden",
      opacity: collapsed ? 0.55 : 1,
      transition: "all 0.2s ease",
    }}>
      {/* Phase header — clickable to toggle (if collapsible) */}
      <button
        type="button"
        onClick={isDirectPhase ? (phase.directAvailable ? () => onNavigate(phase.route!) : undefined) : (canCollapse ? () => setCollapsed((c) => !c) : undefined)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "none",
          border: "none",
          color: "inherit",
          cursor: isDirectPhase ? (phase.directAvailable ? "pointer" : "not-allowed") : (canCollapse ? "pointer" : "default"),
          padding: 0,
          textAlign: "left",
          width: "100%",
        }}
      >
        <span style={{
          width: collapsed ? 26 : 32,
          height: collapsed ? 26 : 32,
          borderRadius: collapsed ? 8 : 10,
          background: phase.color + "22",
          border: `1.5px solid ${phase.color}55`,
          display: "grid",
          placeItems: "center",
          fontSize: collapsed ? "0.7rem" : "0.8rem",
          fontWeight: 800,
          color: phase.color,
          filter: "brightness(1.6)",
          flexShrink: 0,
          transition: "all 0.2s ease",
        }}>
          {phase.icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: collapsed ? "0.82rem" : "0.95rem", fontWeight: 700 }}>{phase.title}</div>
          {!collapsed && <div style={{ fontSize: "0.7rem", opacity: 0.5 }}>{phase.subtitle}</div>}
        </div>
        {canCollapse && <span style={{
          fontSize: "0.7rem",
          opacity: 0.4,
          flexShrink: 0,
          transition: "transform 0.2s ease",
          transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
        }}>
          &#9662;
        </span>}
        {isDirectPhase && phase.directAvailable && <span style={{
          fontSize: "0.75rem",
          fontWeight: 600,
          color: phase.color,
          filter: "brightness(1.4)",
          opacity: 0.9,
          flexShrink: 0,
        }}>
          Open →
        </span>}
      </button>

      {/* Direct action — no separate button, handled by header click */}

      {/* Disabled badge for phases without route */}
      {!collapsed && phase.route == null && phase.directAvailable === false && (
        <div style={{
          padding: "8px 16px",
          borderRadius: 8,
          background: "rgba(255,255,255,0.04)",
          color: "rgba(255,255,255,0.3)",
          fontSize: "0.75rem",
          fontWeight: 600,
          textAlign: "center",
          opacity: 0.5,
        }}>
          Coming soon
        </div>
      )}

      {/* Items — hidden when collapsed */}
      {!collapsed && phase.items.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "0 2px" }}>
          {phase.items.map((item) => (
            <WorkflowItemRow
              key={item.title}
              item={item}
              phaseColor={phase.color}
              isMobile={isMobile}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function WorkflowItemRow({ item, phaseColor, isMobile, onNavigate }: {
  item: WorkflowItem;
  phaseColor: string;
  isMobile: boolean;
  onNavigate: (route: string) => void;
}) {
  const clickable = item.available && item.route;

  return (
    <button
      type="button"
      onClick={() => clickable && onNavigate(item.route!)}
      disabled={!clickable}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: isMobile ? "8px 10px" : "10px 14px",
        borderRadius: 10,
        border: `1px solid ${item.available ? phaseColor + "55" : "rgba(255,255,255,0.06)"}`,
        background: item.available ? phaseColor + "22" : "rgba(255,255,255,0.02)",
        color: item.available ? "#f7f8fb" : "rgba(247,248,251,0.4)",
        cursor: clickable ? "pointer" : "default",
        textAlign: "left",
        minWidth: 0,
        transition: "background 0.15s",
        opacity: item.available ? 1 : 0.6,
        filter: item.available ? "brightness(1.4)" : "none",
      }}
    >
      <span style={{ fontSize: "1.1rem", flexShrink: 0, width: 28, textAlign: "center" }}>
        {item.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600 }}>{item.title}</div>
        <div style={{ fontSize: "0.65rem", opacity: 0.55, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.description}
        </div>
      </div>
      {item.available ? (
        <span style={{
          fontSize: "0.75rem",
          fontWeight: 600,
          color: phaseColor,
          filter: "brightness(1.4)",
          flexShrink: 0,
        }}>
          Open →
        </span>
      ) : (
        <span style={{
          fontSize: "0.58rem",
          fontWeight: 600,
          padding: "3px 8px",
          borderRadius: 999,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          color: "rgba(247,248,251,0.35)",
          flexShrink: 0,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}>
          Soon
        </span>
      )}
    </button>
  );
}
